import { useRef, type ReactNode } from 'react';
import { Captions, Mic, MicOff, Settings } from 'lucide-react';
import {
  IconButton,
  Popover,
  PopoverClose,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from '@wundo/ui';
import { m } from '@/paraglide/messages.js';

export interface PracticeVoiceDockProps {
  microphoneOn: boolean;
  captionsVisible: boolean;
  /**
   * Which microphone and which speaker. Handed in rather than read here,
   * because the two kinds of call switch devices in entirely different ways.
   */
  audioSettings: ReactNode;
  onToggleMicrophone: () => void;
  onToggleCaptions: () => void;
}

/*
 * A dock control that is off still reads as a control: a filled disc, not a
 * bare glyph. The dock floats over the page rather than sitting in a panel, so
 * there is no edge nearby to say where the hit area starts — the fill is the
 * only thing that does. Switched on, the variant paints over it in green.
 */
const DOCK_BUTTON = 'bg-fill hover:bg-line';

/**
 * The three controls a spoken conversation needs, floating over it.
 *
 * The microphone is a switch rather than a button: it has a state somebody can
 * be wrong about, and "muted" is the one thing on this screen that silently
 * changes what the other side hears. Its state is read off the call rather than
 * kept here, so a track that fails to unmute cannot leave the switch lying.
 */
export function PracticeVoiceDock({
  microphoneOn,
  captionsVisible,
  audioSettings,
  onToggleMicrophone,
  onToggleCaptions,
}: PracticeVoiceDockProps) {
  const dock = useRef<HTMLDivElement>(null);

  return (
    <Popover>
      <div className='pointer-events-none absolute inset-x-0 bottom-7 flex justify-center'>
        <div
          ref={dock}
          className='pointer-events-auto flex items-center gap-2.5 rounded-full border border-line bg-surface px-3.5 py-2.5 shadow-menu'
        >
          <IconButton
            role='switch'
            aria-checked={microphoneOn}
            aria-label={
              microphoneOn ? m.practice_session_mic_on() : m.practice_session_mic_off()
            }
            variant={microphoneOn ? 'accent' : 'quiet'}
            size='lg'
            className={microphoneOn ? undefined : DOCK_BUTTON}
            onClick={onToggleMicrophone}
          >
            {microphoneOn ? (
              <Mic size={19} aria-hidden='true' />
            ) : (
              <MicOff size={19} aria-hidden='true' />
            )}
          </IconButton>

          <IconButton
            aria-pressed={captionsVisible}
            aria-label={m.practice_session_captions()}
            variant={captionsVisible ? 'accent' : 'quiet'}
            size='lg'
            className={captionsVisible ? undefined : DOCK_BUTTON}
            onClick={onToggleCaptions}
          >
            <Captions size={19} aria-hidden='true' />
          </IconButton>

          <PopoverTrigger
            render={
              <IconButton
                aria-label={m.practice_session_settings()}
                size='lg'
                className={DOCK_BUTTON}
              />
            }
          >
            <Settings size={19} aria-hidden='true' />
          </PopoverTrigger>
        </div>
      </div>

      {/*
       * Hung off the dock rather than held over the middle of the screen: the
       * gear is pressed to correct a device mid-conversation, and a panel over
       * the orb hides the one thing the user was looking at. A switch made here
       * is remembered under the same names the card before the call writes.
       */}
      <PopoverContent anchor={dock} side='top' sideOffset={10} className='w-80'>
        <PopoverHeader>
          <PopoverTitle>{m.practice_session_audio_title()}</PopoverTitle>
          <PopoverClose aria-label={m.practice_close()} />
        </PopoverHeader>

        <div className='flex flex-col gap-4 px-4 py-3.5'>{audioSettings}</div>
      </PopoverContent>
    </Popover>
  );
}
